// © 2026 AttainIP Engineering
import mixpanel from 'mixpanel-browser';
import { getConsentPreferences } from './consent';
import { MIXPANEL_TOKEN } from '../config/api';

let initialized = false;

export function initMixpanel(): void {
  if (initialized) return;
  if (!MIXPANEL_TOKEN) return;
  if (!getConsentPreferences().analytics) return;

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: import.meta.env.DEV,
    track_pageview: true,
    persistence: 'localStorage',
    record_sessions_percent: 100,
    // Console capture stays OFF. Mixpanel's masking works on the DOM — it blanks page
    // text and form inputs before transmission — but console output is not DOM, so
    // nothing an app logs is masked. One console.log of document or finding content
    // would put customer patent text into a replay tied to a named user, which is the
    // opposite of what our DPA and security answers tell customers (RFC-0018 F36).
    record_console: false,
    record_heatmap_data: true,
  } as Parameters<typeof mixpanel.init>[1]);
  initialized = true;
}

/** Link session replay + events to an authenticated user. */
export function identifyUser(user: { id: string; email: string; display_name: string }): void {
  initMixpanel(); // ensure initialized (handles effect ordering race)
  if (!initialized) return;
  try {
    mixpanel.identify(user.id);
    mixpanel.people.set({
      $email: user.email,
      $name: user.display_name,
    });
  } catch (e) {
    console.warn('[Mixpanel] identify failed:', e);
  }
}

/**
 * Lightweight product-analytics event hook. Fires to Mixpanel when
 * available; otherwise logs to console. Fire-and-forget — never throws.
 *
 * Use for soft signals (e.g. interest registration, future-workflow
 * clicks) where we want a hook in place but don't need durable storage
 * yet. Server-side persistence can be wired later via /v1/events.
 */
export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  console.log(`[event] ${event}`, properties ?? {});
}
